/**
 * DocumentSkeleton — shimmer placeholder shaped like a statutory document page.
 *
 * Mirrors the `.doc-preview` layout (centered letterhead → underlined title →
 * body paragraphs → two-column signature block) so a draft modal can show a
 * document-shaped loading state instead of a blank white page or a bare spinner
 * while the company lookup + IndexedDB restore resolve.
 *
 * Self-contained: all dimensions/colours are inline, so it renders correctly
 * even when DOC_CSS is not in scope. Warm-gray bars (#e7e2d8 / #ddd6c6) match
 * the app's cream theme; a single `animate-pulse` on the root pulses in unison.
 */

const BAR = '#e7e2d8';   // body / sub lines
const BAR_STRONG = '#ddd6c6';   // headings (company name, title)

function Bar({ w = '100%', h = 12, color = BAR, className = '' }) {
  return (
    <div
      className={`rounded ${className}`}
      style={{ width: w, height: h, background: color }}
    />
  );
}

export default function DocumentSkeleton() {
  return (
    <div
      aria-busy="true"
      aria-label="Loading document"
      className="animate-pulse"
      style={{
        padding: '48px 56px',
        minHeight: '760px',
        backgroundColor: '#fff',
      }}
    >
      {/* Letterhead — centered company block over a double rule */}
      <div
        className="flex flex-col items-center gap-[9px] pb-[18px] mb-[22px]"
        style={{ borderBottom: '3px double #e3ddd0' }}
      >
        <Bar w="56%" h={20} color={BAR_STRONG} />
        <Bar w="38%" h={11} />
        <Bar w="28%" h={11} />
      </div>

      {/* Document title — centered */}
      <div className="flex justify-center mb-[26px]">
        <Bar w="46%" h={15} color={BAR_STRONG} />
      </div>

      {/* Body paragraphs */}
      <div className="flex flex-col gap-[11px]">
        {['100%', '96%', '99%', '90%'].map((w, i) => (
          <Bar key={`a-${i}`} w={w} />
        ))}
        <div style={{ height: 12 }} />
        {['98%', '100%', '93%', '97%', '82%'].map((w, i) => (
          <Bar key={`b-${i}`} w={w} />
        ))}
        <div style={{ height: 12 }} />
        {['95%', '88%', '68%'].map((w, i) => (
          <Bar key={`c-${i}`} w={w} />
        ))}
      </div>

      {/* Signature block — two columns */}
      <div className="grid grid-cols-2 gap-10 mt-[64px]">
        {[0, 1].map((c) => (
          <div key={`sig-${c}`} className="flex flex-col gap-[9px]">
            <Bar w="66%" h={12} />
            <Bar w="48%" h={11} color="#ece8df" />
          </div>
        ))}
      </div>
    </div>
  );
}
